import {
  BALANCE,
  CRISIS_DIRECTOR,
  FLEET,
  INFLUENCE,
  INSTABILITY,
  TRADE_GOOD_IDS,
  type FactionId,
} from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import type { MatchSetup } from '@sd/bots';
import { cycleForecast, standings, type CycleForecast, type GameState } from '@sd/sim';
import { clock, num, playerName, seatColor } from './format';

/**
 * The top bar (#53, #56): the player, treasury and rate (with the reserve
 * badge), the stock of goods, the star's instability with what comes next,
 * Prestige and rank, influence, the clock, pause, speed (dev) and the menu;
 * plus the menu and end-of-match dialogs.
 */

const heatOf = (state: GameState) => state.instability.value;
const sign = (v: number) => (v > 0.05 ? 'up' : v < -0.05 ? 'down' : 'flat');
/** «+3,2», «−1,5», «0»: a change per cycle. */
const signed = (locale: Locale, v: number, digits: number) =>
  Math.abs(v) < 0.05 ? '0' : `${v > 0 ? '+' : '−'}${num(locale, Math.abs(v), digits)}`;
export interface HudHandlers {
  togglePause(): void;
  openMenu(open: boolean): void;
  /** Dev tool (#54): offered only when present. */
  setSpeed?(speed: number): void;
  saveLog(): void;
  quit(): void;
  /** The end-of-match screen's body (#59); the bare result line without it. */
  summary?(state: GameState): { readonly title: string; readonly html: string } | null;
  /** Tutorial hints on and off (#59): returns whether they are now on. */
  toggleHints?(): boolean;
  hintsOn?(): boolean;
  /** Sends the end-of-match survey with the log (#61); resolves to whether it arrived. */
  submitSurvey?(answers: Record<string, string>): Promise<boolean>;
}

/** Match speeds of the dev tool: ticks per real tick. */
const SPEEDS = [1, 2, 4] as const;

export class MatchHud {
  private readonly root: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly menu: HTMLElement;
  private readonly ending: HTMLElement;
  private menuOpen = false;
  /** The next cycle's forecast (#101), worked out once per tick. */
  private forecast: { state: GameState; value: CycleForecast | null } | null = null;

  constructor(
    parent: HTMLElement,
    private readonly locale: Locale,
    private readonly setup: MatchSetup,
    private readonly me: string,
    private readonly on: HudHandlers,
  ) {
    const faction = setup.seats.find((s) => s.id === me)?.faction as FactionId;
    // Its own element: a later match gets a fresh HUD and listeners.
    const root = document.createElement('div');
    root.className = 'sd-hud';
    parent.append(root);
    this.root = root;
    const marks = INSTABILITY.thresholds
      .map((v) => `<span class="sd-heat__mark" data-mark="${v}" style="left:${v}%"></span>`)
      .join('');
    root.innerHTML = `
      <header class="sd-topbar">
        <div class="sd-seg sd-seg--you">
          <span class="sd-emblem" style="--seat:${seatColor(me)}"></span>
          <div>
            <div class="sd-value">${t(locale, 'ui.lobby.you')}</div>
            <div class="sd-label">${t(locale, `faction.${faction}.name`)}</div>
          </div>
        </div>
        <div class="sd-seg" data-bind="cash-tip">
          <div class="sd-money">
            <span class="sd-value" data-bind="cash"></span>
            <small class="sd-delta" data-bind="cash-delta"></small>
            <span class="sd-badge sd-badge--alert" data-bind="unsupplied" hidden>${t(locale, 'ui.hud.unsupplied')}</span>
            <span class="sd-badge sd-badge--you" data-bind="reserve" title="${t(locale, 'ui.hud.reserveHint')}" hidden>${t(locale, 'ui.hud.reserve')}</span>
          </div>
          <div class="sd-label" data-bind="rate"></div>
        </div>
        <div class="sd-seg">
          <ul class="sd-goods">
            ${TRADE_GOOD_IDS.map(
              (g) =>
                `<li data-bind="good-tip-${g}"><i class="sd-good-icon" data-good="${g}"></i><span class="sd-good-num"><span data-bind="good-${g}"></span><small class="sd-delta" data-bind="delta-${g}"></small></span></li>`,
            ).join('')}
          </ul>
          <div class="sd-label">${t(locale, 'ui.hud.stock')}</div>
        </div>
        <div class="sd-seg" data-bind="science-tip">
          <div class="sd-money">
            <span class="sd-value" data-bind="science"></span>
            <small class="sd-delta" data-bind="science-delta"></small>
          </div>
          <div class="sd-label">${t(locale, 'ui.hud.science')}</div>
        </div>
        <div class="sd-seg sd-seg--grow">
          <div class="sd-heat">
            <span class="sd-heat__value" data-bind="instability"></span>
            <div class="sd-heat__track"><span class="sd-heat__fill" data-bind="heat"></span>${marks}</div>
            <div class="sd-label">${t(locale, 'ui.instability')} · <span data-bind="heat-hint"></span></div>
          </div>
        </div>
        <div class="sd-seg">
          <div class="sd-value"><span data-bind="prestige"></span> <span class="sd-label" data-bind="rank"></span></div>
          <div class="sd-label">${t(locale, 'ui.prestige')}</div>
        </div>
        <div class="sd-seg">
          <div class="sd-value"><span data-bind="influence"></span><span class="sd-label"> / ${INFLUENCE.max}</span></div>
          <div class="sd-label">${t(locale, 'ui.influence')}</div>
        </div>
        <div class="sd-seg sd-clock">
          <div class="sd-money">
            <span class="sd-value" data-bind="clock"></span>
            <span class="sd-buttons">
              <button class="sd-btn sd-btn--icon" type="button" data-action="pause"></button>
              ${
                on.setSpeed
                  ? `<span class="sd-segmented" role="group" aria-label="${t(locale, 'ui.hud.speed')}" title="${t(locale, 'ui.hud.speed')}">${SPEEDS.map(
                      (x) =>
                        `<button class="sd-btn sd-btn--icon" type="button" data-speed="${x}" aria-pressed="${x === 1}">×${x}</button>`,
                    ).join('')}</span>`
                  : ''
              }
              <button class="sd-btn" type="button" data-action="menu">${t(locale, 'ui.hud.menu')} <kbd>Esc</kbd></button>
            </span>
          </div>
          <div class="sd-label" data-bind="cycle"></div>
        </div>
      </header>
      <div class="sd-overlay" data-overlay="menu" hidden>
        <section class="sd-panel sd-panel--strong sd-dialog" role="dialog" aria-modal="true" aria-labelledby="menu-title">
          <h2 id="menu-title">${t(locale, 'ui.menu.title')}</h2>
          <p class="sd-muted">${t(locale, 'ui.menu.hint')}</p>
          <div class="sd-dialog__actions">
            <button class="sd-btn sd-btn--primary" type="button" data-action="resume">${t(locale, 'ui.menu.resume')}</button>
            ${on.toggleHints ? `<button class="sd-btn" type="button" data-action="hints">${t(locale, on.hintsOn?.() === false ? 'ui.menu.hintsOff' : 'ui.menu.hintsOn')}</button>` : ''}
            <button class="sd-btn" type="button" data-action="save-log">${t(locale, 'ui.menu.saveLog')}</button>
            <button class="sd-btn" type="button" data-action="quit">${t(locale, 'ui.menu.quit')}</button>
          </div>
        </section>
      </div>
      <div class="sd-overlay" data-overlay="ending" hidden>
        <section class="sd-panel sd-panel--strong sd-dialog ${on.summary ? 'sd-dialog--wide' : ''}" role="dialog" aria-labelledby="ending-title">
          <h2 id="ending-title" data-bind="ending-title">${t(locale, 'ui.match.ended')}</h2>
          <p class="sd-dialog__result" data-bind="result"></p>
          <div data-bind="summary"></div>
          <div class="sd-dialog__actions">
            <button class="sd-btn" type="button" data-action="save-log">${t(locale, 'ui.menu.saveLog')}</button>
            <button class="sd-btn sd-btn--primary" type="button" data-action="quit">${t(locale, 'ui.menu.quit')}</button>
          </div>
        </section>
      </div>`;
    this.bar = root.querySelector('.sd-topbar')!;
    this.menu = root.querySelector('[data-overlay="menu"]')!;
    this.ending = root.querySelector('[data-overlay="ending"]')!;

    root.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      if (form.dataset.form !== 'survey' || !on.submitSurvey) return;
      event.preventDefault();
      const answers: Record<string, string> = {};
      for (const [k, v] of new FormData(form))
        if (typeof v === 'string' && v.trim()) answers[k] = v.trim();
      const button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      const status = form.querySelector<HTMLElement>('[data-bind="survey-status"]')!;
      button.disabled = true;
      status.textContent = t(locale, 'survey.sending');
      void on.submitSurvey(answers).then((ok) => {
        status.textContent = t(locale, ok ? 'survey.sent' : 'survey.failed');
        // A failed send can be tried again; a sent one is done.
        button.disabled = ok;
      });
    });
    root.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest('button');
      const action = button?.dataset.action;
      if (button?.dataset.speed) this.pickSpeed(Number(button.dataset.speed));
      else if (action === 'pause') on.togglePause();
      else if (action === 'menu') this.setMenu(true);
      else if (action === 'resume') this.setMenu(false);
      else if (action === 'save-log') on.saveLog();
      else if (action === 'hints' && button && on.toggleHints) {
        button.textContent = t(locale, on.toggleHints() ? 'ui.menu.hintsOn' : 'ui.menu.hintsOff');
      } else if (action === 'quit') on.quit();
    });
  }

  /** Esc toggles the menu; the key is read by position, so any layout works. */
  handleKey(event: KeyboardEvent) {
    if (event.code === 'Escape' && this.ending.hidden) this.setMenu(!this.menuOpen);
  }

  private pickSpeed(speed: number) {
    this.on.setSpeed?.(speed);
    for (const b of this.bar.querySelectorAll<HTMLButtonElement>('[data-speed]')) {
      b.setAttribute('aria-pressed', String(Number(b.dataset.speed) === speed));
    }
  }

  setMenu(open: boolean) {
    this.menuOpen = open;
    this.menu.hidden = !open;
    this.on.openMenu(open);
    if (open) this.menu.querySelector<HTMLButtonElement>('[data-action="resume"]')?.focus();
  }

  update(state: GameState) {
    const me = state.players.find((p) => p.id === this.me);
    if (!me) return;
    const { locale } = this;
    this.text('cash', num(locale, me.cash));
    this.text('rate', t(locale, 'ui.hud.rate', { rate: num(locale, me.currency.rate, 2) }));
    this.bind('reserve').hidden = state.reserve.issuer !== this.me;
    // What the next cycle will bring (#101): output, needs, outposts and upkeep — never purchases.
    if (this.forecast?.state !== state) {
      this.forecast = { state, value: cycleForecast(state, this.me) };
    }
    // The player exists (checked above), so there is a forecast.
    const f = this.forecast.value!;
    const left = clock(f.inTicks);
    const plus = (v: number, digits = 1) => `+${num(locale, v, digits)}`;
    const minus = (v: number, digits = 1) => `−${num(locale, v, digits)}`;
    const lines = (parts: [string, number, (v: number) => string][]) =>
      parts
        .filter(([, v]) => Math.abs(v) >= 0.05)
        .map(([key, v, fmt]) => `• ${t(locale, key, { n: fmt(v) })}`)
        .join('\n');
    this.text('cash-delta', signed(locale, f.cash.net, 0));
    this.bind('cash-delta').dataset.sign = sign(f.cash.net);
    this.bind('cash-tip').title = t(locale, 'ui.hud.cashHint', {
      left,
      net: signed(locale, f.cash.net, 0),
      lines: lines([
        ['ui.hud.forecast.taxes', f.cash.taxes, (v) => plus(v, 0)],
        ['ui.hud.forecast.fleetCash', f.cash.fleet, (v) => minus(v, 0)],
        ['ui.hud.forecast.outpostCash', f.cash.outposts, (v) => minus(v, 0)],
      ]),
    });
    // Unsupplied now, or about to be: the fleets crawl.
    const warn = this.bind('unsupplied');
    warn.hidden = me.supplied && f.supplied;
    warn.title = t(locale, me.supplied ? 'ui.hud.unsuppliedSoon' : 'ui.hud.unsuppliedNow', {
      speed: num(locale, FLEET.unsuppliedSpeed, 1),
      alloys: num(locale, f.goods.alloys.fleet, 1),
    });
    for (const g of TRADE_GOOD_IDS) {
      const x = f.goods[g];
      this.text(`good-${g}`, num(locale, me.goods[g]));
      this.text(`delta-${g}`, signed(locale, x.net, 1));
      this.bind(`delta-${g}`).dataset.sign = sign(x.net);
      const extra =
        g === 'helium3' && f.fleetEnergy > 0
          ? `\n${t(locale, 'ui.hud.forecast.fleetEnergy', { n: num(locale, f.fleetEnergy, 1) })}`
          : '';
      this.bind(`good-tip-${g}`).title = (
        t(locale, 'ui.hud.goodHint', {
          good: t(locale, `resource.${g}`),
          stock: num(locale, me.goods[g]),
          left,
          net: signed(locale, x.net, 1),
          lines: lines([
            ['ui.hud.forecast.made', x.made, plus],
            ['ui.hud.forecast.outputs', x.outposts, plus],
            ['ui.hud.forecast.used', x.used, minus],
            ['ui.hud.forecast.fleetAlloys', x.fleet, minus],
          ]),
        }) + extra
      )
        .replace(/\n+/g, '\n')
        .trim();
    }
    this.text('science', num(locale, me.planet.science));
    this.text('science-delta', signed(locale, f.science, 1));
    this.bind('science-delta').dataset.sign = sign(f.science);
    this.bind('science-tip').title = t(locale, 'ui.hud.scienceHint', {
      science: num(locale, f.science, 1),
      left,
    });
    const next = INSTABILITY.thresholds.find((v) => v > heatOf(state));
    const final = clock(CRISIS_DIRECTOR.finalWarningTick);
    this.text(
      'heat-hint',
      next !== undefined
        ? t(locale, 'ui.hud.heatHint', { next, final })
        : t(locale, 'ui.hud.heatFinal', { final }),
    );
    const rank = standings(state.players).findIndex((x) => x.playerId === this.me) + 1;
    this.text('rank', t(locale, 'ui.hud.rank', { rank }));
    this.text('influence', num(locale, me.influence));
    const heat = state.instability.value;
    this.text('instability', num(locale, heat));
    const fill = this.bind('heat');
    fill.style.width = `${Math.min(100, heat)}%`;
    // The gradient spans the whole track, so the fill shows its own part of it.
    fill.style.setProperty('--track-width', `${(100 / Math.max(1, heat)) * 100}%`);
    for (const mark of this.bar.querySelectorAll<HTMLElement>('[data-mark]')) {
      mark.classList.toggle('sd-heat__mark--passed', Number(mark.dataset.mark) <= heat);
    }
    this.text('prestige', num(locale, me.prestige, 1));
    this.text('clock', clock(state.tick));
    this.text(
      'cycle',
      t(locale, 'ui.hud.cycle', {
        n: Math.floor(state.tick / BALANCE.cycleTicks) + 1,
        left: clock(BALANCE.cycleTicks - (state.tick % BALANCE.cycleTicks)),
      }),
    );
    const pause = this.bar.querySelector<HTMLButtonElement>('[data-action="pause"]')!;
    const mine = state.pause?.by === this.me;
    pause.textContent = mine ? '▶' : `❚❚ ${me.pausesLeft}`;
    pause.disabled = !mine && (me.pausesLeft === 0 || state.pause !== null);
    pause.title = mine
      ? t(locale, 'ui.menu.resume')
      : t(locale, 'ui.pausesLeft', { count: me.pausesLeft });

    if (state.result && this.ending.hidden) {
      const winner = state.result.winner;
      const winners = state.result.winners;
      const summary = this.on.summary?.(state);
      if (summary) {
        // The summary says it all: its title replaces the generic one.
        this.bind('summary').innerHTML = summary.html;
        this.bind('result').hidden = true;
        this.text('ending-title', summary.title);
      }
      this.text(
        'result',
        `${t(locale, `victory.${state.result.kind}.name`)} · ${
          winner
            ? t(locale, 'ui.match.winner', {
                name: winners.map((id) => playerName(locale, this.setup, id)).join(', '),
              })
            : t(locale, 'ui.match.nobody')
        }`,
      );
      this.ending.hidden = false;
      this.setMenu(false);
    }
  }

  dispose() {
    this.root.remove();
  }

  private bind(name: string): HTMLElement {
    return this.root.querySelector<HTMLElement>(`[data-bind="${name}"]`)!;
  }

  private text(name: string, value: string) {
    const node = this.bind(name);
    if (node.textContent !== value) node.textContent = value;
  }
}
